import {
  isSchoolEventDone,
  isSchoolEventDuplicate,
  resolveSchoolEvent,
} from "./resolveSchoolEvent";
import { isSchoolEventRemindEnabled } from "./schoolReminderPrefs";
import { buildGoogleCalendarUrl, buildOutlookCalendarUrl } from "./googleCalendarLink";
import { buildKitaReminderCardMessage } from "./schoolSetupChat";

/**
 * Resolve one mail extraction row for push deep-link / chat reminder card.
 * @param {{
 *   extractionId: string,
 *   eventIndex: number,
 *   mailRows?: object[],
 *   listItems?: object[],
 * }} args
 */
export function resolveKitaReminderFromMail({
  extractionId,
  eventIndex,
  mailRows = [],
  listItems = [],
}) {
  const row = mailRows.find((r) => r.id === extractionId);
  const rawEvent = row?.events?.[eventIndex];
  const resolved = resolveSchoolEvent(rawEvent, row?.corrections, eventIndex);
  if (!resolved || !extractionId) return null;

  const listItem = listItems.find(
    (item) => item.extractionId === extractionId && item.eventIndex === eventIndex,
  );

  const remindEnabled =
    typeof listItem?.remindEnabled === "boolean"
      ? listItem.remindEnabled
      : isSchoolEventRemindEnabled(rawEvent, row?.corrections, eventIndex);

  return {
    extractionId,
    eventIndex,
    event: {
      ...resolved,
      title: resolved.title || listItem?.title,
      date: resolved.date || listItem?.date,
      time: resolved.time ?? listItem?.time,
      all_day: resolved.all_day ?? listItem?.all_day,
      action_type: resolved.action_type || listItem?.action_type,
    },
    remindEnabled,
    calUrl: buildGoogleCalendarUrl(resolved),
    outlookUrl: buildOutlookCalendarUrl(resolved),
  };
}

/**
 * @param {{
 *   reminderKind?: string|null,
 *   extractionId: string,
 *   eventIndex: number,
 *   event: object,
 *   remindEnabled: boolean,
 *   calUrl?: string|null,
 *   outlookUrl?: string|null,
 * }} args
 */
export function buildKitaReminderChatMessage({
  reminderKind,
  extractionId,
  eventIndex,
  event,
  remindEnabled,
  calUrl,
  outlookUrl,
}) {
  return {
    text: buildKitaReminderCardMessage({
      title: event.title,
      date: event.date,
      remindEnabled,
      reminderKind,
    }),
    content: "kita-reminder-card",
    reminderCard: {
      extractionId,
      eventIndex,
      event,
      remindEnabled,
      reminderKind,
      calUrl: calUrl || buildGoogleCalendarUrl(event),
      outlookUrl: outlookUrl || buildOutlookCalendarUrl(event),
    },
  };
}

/** @param {URLSearchParams} searchParams */
export function parseKitaReminderParams(searchParams) {
  if (!searchParams || searchParams.get("kita_reminder") !== "1") return null;
  const eventIndexRaw = searchParams.get("event_index");
  const eventIndex = Number.parseInt(eventIndexRaw || "0", 10);
  return {
    extractionId: searchParams.get("extraction_id") || null,
    eventIndex: Number.isFinite(eventIndex) ? eventIndex : 0,
    reminderKind: searchParams.get("reminder_kind") || null,
    scope: searchParams.get("scope") || null,
    reminderDate: searchParams.get("reminder_date") || null,
  };
}

/**
 * Reminder cards for one deadline day (evening digest deep link).
 * @param {{
 *   mailRows?: object[],
 *   listItems?: object[],
 *   reminderDate?: string|null,
 * }} args
 */
export function resolveKitaRemindersForDate({
  mailRows = [],
  listItems = [],
  reminderDate,
}) {
  const date = String(reminderDate || "");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return [];

  const cards = [];
  for (const row of mailRows || []) {
    (row.events || []).forEach((rawEvent, index) => {
      if (isSchoolEventDone(row.corrections, index)) return;
      if (isSchoolEventDuplicate(rawEvent)) return;
      const resolved = resolveSchoolEvent(rawEvent, row.corrections, index);
      if (!resolved || isSchoolEventDuplicate(resolved)) return;
      const actionType = String(resolved.action_type || "").toLowerCase();
      if (actionType === "info" || resolved.date !== date) return;
      const card = resolveKitaReminderFromMail({
        extractionId: row.id,
        eventIndex: index,
        mailRows,
        listItems,
      });
      if (!card?.remindEnabled) return;
      cards.push(card);
    });
  }

  cards.sort((a, b) => {
    const time = String(a.event?.time || "").localeCompare(String(b.event?.time || ""));
    if (time !== 0) return time;
    return String(a.event?.title || "").localeCompare(String(b.event?.title || ""), "de");
  });
  return cards;
}

/** @param {URLSearchParams} searchParams */
export function parseBriefingPushParams(searchParams) {
  if (!searchParams || searchParams.get("briefing_push") !== "1") return null;
  const kindRaw = searchParams.get("kind");
  const signalsRaw = searchParams.get("signals");
  return {
    kind: kindRaw === "evening" ? "evening" : "morning",
    briefingDate: searchParams.get("briefing_date") || null,
    signals: signalsRaw
      ? signalsRaw.split(",").map((s) => s.trim()).filter(Boolean)
      : [],
  };
}

/**
 * Push taps that should land on the reminder/task — not the daily briefing dump
 * (weather, clothing tip, self-care, extra prompts).
 * @param {URLSearchParams} searchParams
 */
export function parseKitaExtractionParams(searchParams) {
  if (!searchParams || searchParams.get("kita_extraction") !== "1") return null;
  const extractionId = searchParams.get("extraction_id")?.trim() || null;
  return extractionId ? { extractionId } : null;
}

export function parseKitaExtractionBatchParams(searchParams) {
  if (!searchParams || searchParams.get("kita_extraction_batch") !== "1") return null;
  return {
    batchDate: searchParams.get("batch_date")?.trim() || null,
  };
}

export function isFocusedChatPushDeepLink(searchParams) {
  if (!searchParams) return false;
  return (
    searchParams.get("kita_reminder") === "1" ||
    searchParams.get("kita_extraction") === "1" ||
    searchParams.get("kita_extraction_batch") === "1" ||
    searchParams.get("briefing_push") === "1" ||
    Boolean(searchParams.get("prompt")?.trim()) ||
    Boolean(searchParams.get("capture")?.trim())
  );
}

const BRIEFING_PUSH_WEATHER_IDS = new Set(["weather", "tomorrow-weather"]);

/**
 * Sections to show in chat after a briefing push tap — match the notification,
 * not the full in-app briefing (no clothing tip / self-care / leftover KiTa).
 * @param {Array<{ id?: string, text?: string }>} sections
 * @param {{ kind?: 'morning'|'evening', signals?: string[] }} [opts]
 */
export function sectionsForBriefingPush(sections, { kind = "morning", signals = [] } = {}) {
  const tags = new Set(Array.isArray(signals) ? signals : []);
  const hasSignals = tags.size > 0;
  const wantHoliday = !hasSignals || tags.has("holiday");
  const wantKitaToday = hasSignals ? tags.has("kita_today") : kind === "morning";
  const wantWeather = hasSignals ? tags.has("weather_shift") : kind === "evening";

  return (sections || []).filter((section) => {
    if (!section?.text) return false;
    const id = section.id || "";
    if (id === "self-care" || id === "schedule-upcoming" || id === "schedule-tomorrow") {
      return false;
    }
    if (BRIEFING_PUSH_WEATHER_IDS.has(id)) return wantWeather;
    if (id === "regional-calendar") return wantHoliday;
    if (id === "schedule-today") return wantKitaToday;
    return false;
  });
}

/**
 * @param {Array<{ label?: string, text?: string }>} sections
 */
export function briefingTextFromSections(sections) {
  return (sections || [])
    .filter((s) => s && s.text)
    .map((s) => (s.label ? `${s.label}: ${s.text}` : s.text))
    .join("\n");
}

function briefingPushChatText(sections) {
  return (sections || [])
    .filter((s) => s && s.text)
    .map((s) => s.text)
    .join(" ");
}

/**
 * @param {{ chatText?: string, sections?: object[] }} briefing
 * @param {{ kind?: 'morning'|'evening', signals?: string[], fromPush?: boolean }} [opts]
 */
export function briefingChatMessageFromPayload(
  briefing,
  { kind = "morning", signals = [], fromPush = false } = {},
) {
  let text;
  if (fromPush) {
    text =
      briefingPushChatText(sectionsForBriefingPush(briefing?.sections, { kind, signals })) ||
      "Briefing geladen.";
  } else {
    text =
      briefing?.chatText || briefingTextFromSections(briefing?.sections) || "Briefing geladen.";
  }
  return {
    text,
    showAgenda: true,
    ...(kind === "morning" && !fromPush
      ? {
          action: { type: "share_briefing" },
          actionLabel: "An Partner senden",
        }
      : {}),
  };
}
